<template>
  <div :class="_fs_class">
    <cat-label
      :label-id="id + '_lbl'"
      :for-id="id + '-attribute-decimal'"
      :label="label"
      :placeholder="placeholder"
      :optional="!mandatory"
      :errors="errors"
      :readonly="readonly"
      :inline="help && !!help.url"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <slot></slot>
    <div class="input-holder">
      <cat-formatted-number
        v-if="formatOnInput"
        :id="id + '-attribute-decimal'"
        :label-handled="true"
        :placeholder="placeholder"
        :value="model"
        :mandatory="mandatory"
        :readonly="readonly"
        :max-length="maxLength"
        :locale="locale"
        :decimal-digits="decimalDigits"
        :errors="errors && errors.length > 0"
        :allowNegative="allowNegative"
        :ariaDescribedby="hint ? id + '_hint' : null"
        @ui-change="_onChange"
      />
      <cat-input
        v-else
        :id="id + '-attribute-decimal'"
        :value="model"
        :class="_ip_class"
        :placeholder="placeholder"
        :error="errors && errors.length > 0"
        :mandatory="mandatory"
        :readonly="readonly"
        :mask="numberMask"
        :regex="numberRegex"
        :label-handled="true"
        :ariaDescribedby="hint ? id + '_hint' : null"
        @ui-change="_onChange"
        @ui-input="_onInput"
        @ui-blur="_onBlur"
        @ui-key-up="_propogate('ui-key-up', $event)"
        @ui-key-press="_propogate('ui-key-press', $event)"
        @ui-key-down="_propogate('ui-key-down', $event)"
      />
      <slot name="after-input"></slot>
      <cat-errors v-if="errors" :errors="errors" />
      <div role="presentation" class="spacer"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
//import jQuery from "jquery";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatInput from "@/components/formelements/cat-input.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatFormattedNumber from "@/components/formelements/cat-formatted-number";
import AttributeMixin from "./attribute-mixin";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger";
export default defineComponent({
  name: "CatAttributeDecimal",
  components: {
    CatFormattedNumber,
    CatErrors,
    CatInput,
    CatHelp,
    CatLabel,
    CatHelpTrigger,
    CatHint
  },
  mixins: [AttributeMixin],
  props: {
    id: {
      type: [Boolean, Number, String],
      description: "Sets the ID attributes for child elements."
    },
    blurHandler: {
      type: Function,
      description: "Not in use."
    },
    box: [Boolean],
    outline: [Boolean],
    value: [Number, String],
    errors: [Array],
    readonly: [Boolean],
    label: {
      type: String,
      description: "Sets the label to display above input fields."
    },
    hint: {
      type: String,
      description: "Sets hint text to appear above input field."
    },
    mandatory: {
      type: [Boolean, String],
      default: false,
      description:
        "Sets aria-required attributes for input fields and label, also sets css class."
    },
    placeholder: {
      type: String,
      description: "Sets the placeholder text for a field."
    },
    size: [String],
    inline: {
      type: Boolean,
      default: false,
      description: "Sets css class - styling related."
    },
    min: {
      type: [Number, String],
      description: "Minimum accepted value. Sets to this if less."
    },
    max: {
      type: [Number, String],
      description: "Maximum accepted value. Sets to this if exceeded."
    },
    maxLength: {
      type: [Number, String],
      description:
        "Maximum number digits including the decimal digits. Decimal and thousand grouping separator are excluded. Set the value from 0 to 15."
    },
    locale: {
      type: String,
      description:
        "The locale to use to format the number if formatOnInput prop is true."
    },
    numberMask: {
      type: String,
      default: "0123456789."
    },
    numberRegex: {
      type: String,
      default: "^\\d*(\\.\\d{0,2})?$"
    },
    decimalDigits: {
      type: [Number, String],
      default: 2,
      description: "Number of decimal fraction digits."
    },
    help: {
      type: Object,
      description: "Sets the expandable Help Text above an input field."
    },
    formatOnInput: {
      type: Boolean,
      default: false,
      description:
        "Flag to indicate if automatically format the number while typing."
    },
    allowNegative: {
      type: Boolean,
      default: false,
      description: "Specifies whether negative values are allowed."
    }
  },
  data: function () {
    return {
      model: this.value ? this.value : "",
      working: this.value ? this.value : ""
    };
  },
  watch: {
    value: function (newval) {
      this.model = newval;
      this.working = newval;
    }
  },

  methods: {
    _onChange: function (event) {
      if (!this.isValid(event.value)) {
        this.model = this.working;
        return;
      }
      this.working = this.model;
      this.$emit("ui-change", {
        event: event.event,
        value: event.value,
        id: this.id ? this.id : null
      });
    },
    _onBlur: function (event) {
      this.$emit("ui-blur", {
        event: event.event,
        value: event.value,
        id: this.id ? this.id : null
      });
    },
    _onInput: function (event) {
      this.model = event.event.target.value; //two way binding
      this.working = this.model;
      this.$emit("ui-input", {
        event: event.event,
        value: event.model,
        id: this.id ? this.id : null
      });
    },
    _onInputOrig: function (value) {
      this.$emit("input", value);
    },
    isValid: function (value) {
      if (this.min != null) {
        if (this.model < parseFloat(this.min)) {
          return false;
        }
      }
      if (this.max != null) {
        if (this.model > parseFloat(this.max)) {
          return false;
        }
      }
      return true;
    }
  },

  computed: {
    _fs_class: function () {
      var css = {
        fieldset: true,
        "cat-number-field": true,
        invalid:
          this.errors &&
          this.errors.length > 0 &&
          this.errors.some(function (r) {
            return r.level <= 1;
          }),
        required: this.mandatory,
        optional: !this.mandatory,
        inline: this.inline,
        "readonly-list": this.readonly
      };

      css.warning =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 2 && !css.invalid;
        });
      css.infomessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 3 && !css.invalid;
        });
      css.debugmessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 4;
        });
      return css;
    },
    _ip_class: function () {
      return {
        small: this.size == "small",
        medium: this.size == "medium",
        large: this.size == "large"
      };
    }
  }
});
</script>
